import { useMemo, useState } from 'react'
import { download, money, shortDate, chartPoints } from '../api'
import useResource from '../useResource'
import { ExportButton, MetricCard, PageHeader, SectionTitle, StatePanel } from './Common'

function CashChart({ series }) {
  const points = chartPoints(series?.due_date, series?.promise_to_pay)
  const displayed = points.length > 42 ? points.slice(-42) : points
  const max = Math.max(...displayed.flatMap((point) => [point.due, point.promised]), 1)
  return <div className="col-cash-chart" role="img" aria-label="Due date and promise to pay cash flow series">
    <div className="col-chart-y"><span>{money(max, 'ILS', true)}</span><span>{money(max / 2, 'ILS', true)}</span><span>₪0</span></div>
    <div className="col-cash-plot">{displayed.map((point, index) => (
      <div className="col-cash-column" key={point.date} title={`${shortDate(point.date)} · Due ${money(point.due)} · Promised ${money(point.promised)}`}>
        <div><i className="due" style={{ height: `${point.due / max * 100}%` }} /><i className="promised" style={{ height: `${point.promised / max * 100}%` }} /></div>
        {(displayed.length < 18 || index % Math.ceil(displayed.length / 12) === 0) && <span>{point.date.slice(5, 7)}/{point.date.slice(2, 4)}</span>}
      </div>
    ))}</div>
  </div>
}

export default function CashFlow({ businessUnit = '' }) {
  const [granularity, setGranularity] = useState('monthly')
  const [start, setStart] = useState('2025-09-01')
  const [end, setEnd] = useState('2026-08-31')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(null)
  const params = useMemo(() => ({ granularity, start, end, business_unit: businessUnit }), [granularity, start, end, businessUnit])
  const resource = useResource('cash-flow', params)
  const data = resource.data
  const doExport = async () => {
    setExporting(true); setExportError(null)
    try { await download('cash-flow/export', `cash-flow-${granularity}.xlsx`, params) }
    catch (error) { setExportError(error) }
    finally { setExporting(false) }
  }
  return <div className="col-page">
    <PageHeader title="Cash flow" description="Compare contractual due dates with customer promises to pay." actions={<ExportButton onClick={doExport} busy={exporting} />} />
    <div className="col-toolbar cash">
      <div className="col-segmented"><button className={granularity === 'monthly' ? 'active' : ''} onClick={() => setGranularity('monthly')}>Monthly</button><button className={granularity === 'daily' ? 'active' : ''} onClick={() => setGranularity('daily')}>Daily</button></div>
      <label>From<input className="col-input" type="date" value={start} onChange={(event) => setStart(event.target.value)} /></label>
      <label>To<input className="col-input" type="date" value={end} onChange={(event) => setEnd(event.target.value)} /></label>
    </div>
    {exportError && <div className="col-inline-alert">{exportError.message}</div>}
    <StatePanel {...resource} empty={data && !data.series?.due_date?.length} onRetry={resource.retry} emptyTitle="No cash flow in this period" emptyText="Choose a wider date range to include open invoice due dates or promises.">
      {data && <>
        <div className="col-metric-grid three cash-metrics">
          <MetricCard label="Total due" value={data.kpis?.total_due} icon="payments" format="money" />
          <MetricCard label="Promised" value={data.kpis?.total_promised} icon="handshake" tone="teal" format="money" />
          <MetricCard label="Uncovered gap" value={data.kpis?.gap} icon="difference" tone="peach" format="money" detail="Due minus promised" />
        </div>
        {data.currency_summary?.unvalued_invoice_count > 0 && <div className="col-inline-alert">The NIS series excludes {data.currency_summary.unvalued_invoice_count} invoice{data.currency_summary.unvalued_invoice_count === 1 ? '' : 's'} needing balance reconciliation or FX evidence. Original totals: {Object.entries(data.currency_summary.unvalued_totals_by_currency || {}).map(([currency, total]) => money(total, currency)).join(' · ')}.</div>}
        <section className="col-card col-cash-panel">
          <SectionTitle title="Expected receipts" text={`${granularity === 'monthly' ? 'Monthly' : 'Daily'} values in NIS`} action={<div className="col-legend"><span><i className="due" />Due date</span><span><i className="promised" />Promise to pay</span></div>} />
          <CashChart series={data.series} />
        </section>
      </>}
    </StatePanel>
  </div>
}
